import { Injectable } from '@angular/core';
import { Brand } from 'src/app/components/company/models/new/brand.model';
import { Category } from '../models/new/category';
import { Color, Gender, Size, Style, StyleFilters, WarehouseArticle } from '../models/new/style';
import { StyleDescription } from '../models/new/style-description';
import { StyleGroup } from '../models/new/style-group';
import { Collection } from '../models/new/collection';
import { Purpose } from '../models/new/purpose';
import { Team } from '../models/new/team';
import { Status } from '../models/new/status';
import { Order } from '../models/new/order';
import _ from 'lodash';
import { Warehouse } from 'src/app/shared/types/warehouse.model';

@Injectable({
	providedIn: 'root'
})
export class StyleMapper {
	toStyle(_style: any) {
		const schemas = Array.isArray(_style.schema) ? _style.schema : _style.schema ? [_style.schema] : [];
		const schemaTeamColors = _.flattenDeep(schemas.map((s: any) => s.schemaTeamColors ?? []));
		const collectionSchema = schemas.find((s: any) => s.collection);

		return new Style({
			brand: new Brand({
				id: _style.brand.id,
				name: _style.brand.name
			}),
			category: new Category({
				id: _style.category.id,
				name: _style.category.name
			}),
			status: this.mapStyleStatus(_style),
			modelId: _style.modelId,
			code: _style.code,
			name: _style.name,

			styleGroup: _style.styleGroup
				? new StyleGroup({
						id: _style.styleGroup.id,
						name: _style.styleGroup.name
					})
				: undefined,
			styleDescription: _style.styleDescription
				? new StyleDescription({
						id: _style.styleDescription.id,
						name: _style.styleDescription.name
					})
				: undefined,
			collection: collectionSchema?.collection
				? new Collection({
						id: collectionSchema.collection.id,
						name: collectionSchema.collection.name
					})
				: undefined,

			gender: _style.gender
				? new Gender({
						id: _style.gender.id,
						name: _style.gender.name.toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase())
					})
				: undefined,
			/*purpose: _style.purpose
				? new Purpose({
						id: _style.purpose.id,
						name: _style.purpose.name
					})
				: undefined,*/

			teams: _.uniqBy(
				schemaTeamColors.filter((stc: any) => stc.team),
				(stc: any) => stc.team.id
			).map((stc: any) => new Team({ id: stc.team.id, code: stc.team.code, name: stc.team.name })),
			orders: _style.orderStyles
				? _.uniqBy(_style.orderStyles, (os: any) => os.order.id).map(
						(os: any) =>
							new Order({
								id: os.order.id,
								creationDate: os.order.creationDate,
								status: new Status({ id: os.order.orderStatus.id, name: os.order.orderStatus.name })
							})
					)
				: [],
			colors: _.uniqBy(
				schemaTeamColors.filter((stc: any) => stc.color),
				(stc: any) => stc.color.id
			).map(
				(stc: any) =>
					new Color({
						id: stc.color.id,
						code: stc.color.code,
						name: stc.color.finalColorName || stc.color.name,
						size: stc.color.size ? new Size({ id: stc.color.size.id, name: stc.color.size.name }) : undefined
					})
			),
			warehouseArticles: _style.warehouseArticles
				? _style.warehouseArticles.map(
						(wa: any) =>
							new WarehouseArticle({
								id: wa.id,
								availableQty: wa.availableQty,
								operationType: wa.operationType,
								color: wa.color ? new Color({ id: wa.color.id, code: wa.color.code, name: wa.color.autoGeneratedColorName || wa.color.name }) : undefined,
								size: wa.size ? new Size({ id: wa.size.id, name: wa.size.name }) : undefined,
								warehouse: wa.warehouse ? new Warehouse({ id: wa.warehouse.id, code: wa.warehouse.code, description: wa.warehouse.description }) : undefined
							})
					)
				: []
		});
	}

	private mapStyleStatus(style: any): Status | undefined {
		const current = style.currentStyleStatus;
		const entry = Array.isArray(current) ? current[0] : current;
		const styleStatus = entry?.styleStatus ?? style.status;

		return styleStatus ? new Status({ id: styleStatus.id, name: styleStatus.name }) : undefined;
	}

	filtersToUrl(filters: StyleFilters) {
		return {
			brands: filters.brands ? filters.brands.map((brand) => brand.id) : undefined,
			categories: filters.categories ? filters.categories.map((category) => category.id) : undefined,
			styleGroups: filters.styleGroups ? filters.styleGroups.map((styleGroup) => styleGroup.id) : undefined,
			styleDescriptions: filters.styleDescriptions ? filters.styleDescriptions.map((styleDescription) => styleDescription.id) : undefined,
			collections: filters.collections ? filters.collections.map((collection) => collection.id) : undefined,
			purposes: filters.purposes ? filters.purposes.map((purpose) => purpose.id) : undefined,
			genders: filters.genders ? filters.genders.map((gender) => gender.id) : undefined,
			teams: filters.teams ? filters.teams.map((team) => team.id) : undefined,
			styleCode: filters.styleCode || undefined,
			statuses: filters.statuses ? filters.statuses.map((status) => status.id) : undefined,
			warehouses: filters.warehouses ? filters.warehouses.map((warehouse) => warehouse.id) : undefined,
			page: filters.page || 1,
			size: filters.size || 20
		};
	}

	urlToFilters(url: any) {
		return new StyleFilters({
			brands: url.brands ? (Array.isArray(url.brands) ? url.brands.map((id: any) => new Brand({ id: Number(id) })) : [new Brand({ id: Number(url.brands) })]) : undefined,
			warehouses: url.warehouses ? (Array.isArray(url.warehouses) ? url.warehouses.map((id: any) => new Warehouse({ id: Number(id) })) : [new Warehouse({ id: Number(url.warehouses) })]) : undefined,
			categories: url.categories ? (Array.isArray(url.categories) ? url.categories.map((id: any) => new Category({ id: Number(id) })) : [new Category({ id: Number(url.categories) })]) : undefined,
			styleGroups: url.styleGroups ? (Array.isArray(url.styleGroups) ? url.styleGroups.map((id: any) => new StyleGroup({ id: Number(id) })) : [new StyleGroup({ id: Number(url.styleGroups) })]) : undefined,
			styleDescriptions: url.styleDescriptions ? (Array.isArray(url.styleDescriptions) ? url.styleDescriptions.map((id: any) => new StyleDescription({ id: Number(id) })) : [new StyleDescription({ id: Number(url.styleDescriptions) })]) : undefined,
			collections: url.collections ? (Array.isArray(url.collections) ? url.collections.map((id: any) => new Collection({ id: Number(id) })) : [new Collection({ id: Number(url.collections) })]) : undefined,
			purposes: url.purposes ? (Array.isArray(url.purposes) ? url.purposes.map((id: any) => new Purpose({ id: Number(id) })) : [new Purpose({ id: Number(url.purposes) })]) : undefined,
			genders: url.genders ? (Array.isArray(url.genders) ? url.genders.map((id: any) => new Gender({ id: Number(id) })) : [new Gender({ id: Number(url.genders) })]) : undefined,
			teams: url.teams ? (Array.isArray(url.teams) ? url.teams.map((id: any) => new Team({ id: Number(id) })) : [new Team({ id: Number(url.teams) })]) : undefined,
			statuses: url.statuses ? (Array.isArray(url.statuses) ? url.statuses.map((id: any) => new Status({ id: Number(id) })) : [new Status({ id: Number(url.statuses) })]) : undefined,
			styleCode: url.styleCode || undefined,
			page: Number(url.page || 1),
			size: Number(url.size || 20)
		});
	}

	toWhereClause(filters: StyleFilters) {
		return {
			brand: filters.brands?.length ? { id: { in: filters.brands.map((b) => b.id) } } : undefined,
			category: filters.categories?.length ? { id: { in: filters.categories.map((c) => c.id) } } : undefined,

			code: filters.styleCode ? { contains: filters.styleCode } : undefined,
			styleGroup: filters.styleGroups?.length ? { id: { in: filters.styleGroups.map((sg) => sg.id) } } : undefined,
			styleDescription: filters.styleDescriptions?.length ? { id: { in: filters.styleDescriptions.map((sd) => sd.id) } } : undefined,
			...(filters.collections?.length && { schema: { some: { collection: { id: { in: filters.collections.map((col) => col.id) } } } } }),
			...(filters.teams?.length && { schema: { some: { schemaTeamColors: { some: { team: { id: { in: filters.teams.map((t) => t.id) } } } } } } }),
			purpose: filters.purposes?.length ? { id: { in: filters.purposes.map((p) => p.id) } } : undefined,
			gender: filters.genders?.length ? { id: { in: filters.genders.map((g) => g.id) } } : undefined,
			...(filters.statuses?.length && {
				currentStyleStatus: { some: { styleStatus: { id: { in: filters.statuses.map((s) => s.id) } } } }
			}),
			...(filters.warehouses?.length && {
				warehouseArticles: {
					some: {
						warehouseId: { eq: filters.warehouses[0].id },
						availableQty: { gt: 0 },
						operationType: { neq: 'D' }
					}
				}
			})
		};
	}
}
